/**
 * Antrenman programı üreticisi — saf. Girdi al, çıktı ver, yan etki yok.
 *
 * Temel fikir: program hareket listesi değil, **hareket kalıbı** listesidir.
 * Her antrenman gününde her kalıptan bir hareket olur; hangi hareketin
 * geldiğini elindeki ekipman belirler. Böylece ekipmanın değişince program
 * bozulmuyor, sadece aynı kalıbın başka bir varyantı geliyor.
 */

export type Pattern = 'squat' | 'hinge' | 'push_h' | 'push_v' | 'pull_h' | 'pull_v' | 'core' | 'accessory'
export type Equipment =
  | 'bodyweight' | 'bands' | 'pullup_bar' | 'dumbbell' | 'kettlebell'
  | 'barbell' | 'machine' | 'cable' | 'ball'
export type Level = 'beginner' | 'intermediate' | 'expert'

export interface CatalogExercise {
  slug: string
  nameTr: string
  nameEn: string
  pattern: Pattern | string
  equipment: Equipment | string
  level: Level | string
  primaryMuscles: string[]
  secondaryMuscles: string[]
  instructions: string[]
  instructionsAreTurkish: boolean
  images: string[]
  curated: boolean
}

export const PATTERN_LABEL: Record<Pattern, string> = {
  squat: 'Çömelme',
  hinge: 'Kalça menteşesi',
  push_h: 'Yatay itme',
  push_v: 'Dikey itme',
  pull_h: 'Yatay çekme',
  pull_v: 'Dikey çekme',
  core: 'Gövde',
  accessory: 'Tamamlayıcı',
}

export const EQUIPMENT_LABEL: Record<Equipment, string> = {
  bodyweight: 'Vücut ağırlığı',
  bands: 'Direnç bandı',
  // "Vücut ağırlığı" her şeyi kapsamıyor: barfiks için asılacak bir yer lazım.
  // Ayrı seçenek olmasaydı bar olmadan barfiks önerilmeye devam ederdi.
  pullup_bar: 'Barfiks barı',
  dumbbell: 'Dambıl',
  kettlebell: 'Kettlebell',
  barbell: 'Halter (barbell)',
  machine: 'Makineler',
  cable: 'Kablo (cable)',
  ball: 'Pilates / sağlık topu',
}

/**
 * Kas sırası — hem hacim tablosunda hem "hareket ekle" listesinde aynı sıra.
 * Büyük kas gruplarından küçüklere: listeyi tarayan biri önce göğüs/sırt/bacak
 * görsün, kol ve baldır sonra gelsin.
 */
export const MUSCLE_ORDER = [
  'chest', 'lats', 'middle back', 'shoulders', 'quadriceps', 'hamstrings',
  'glutes', 'abdominals', 'triceps', 'biceps', 'lower back', 'traps', 'calves',
]

export const MUSCLE_TR: Record<string, string> = {
  quadriceps: 'ön bacak', hamstrings: 'arka bacak', glutes: 'kalça', calves: 'baldır',
  chest: 'göğüs', shoulders: 'omuz', triceps: 'triseps', biceps: 'biseps',
  lats: 'kanat (lat)', 'middle back': 'orta sırt', 'lower back': 'bel', traps: 'trapez',
  abdominals: 'karın', forearms: 'ön kol', adductors: 'iç bacak', abductors: 'dış bacak',
  neck: 'boyun',
}

/** A ve B günleri — birlikte tüm vücudu kapsar, üst üste binmez. */
export const DAY_A: Pattern[] = ['squat', 'push_h', 'pull_h', 'core']
export const DAY_B: Pattern[] = ['hinge', 'push_v', 'pull_v', 'core']

/**
 * Bölünme (split) şablonları.
 *
 * Hepsi aynı sekiz kalıptan kuruluyor; fark kalıpların günlere nasıl
 * dağıtıldığı. Seçim bir zevk meselesi değil, **gün sayısının** meselesi:
 *
 *  - 2–3 gün → tüm vücut. Split yaparsan her kas haftada 1 kez çalışır ve
 *    haftalık hacmin tamamını tek seansa yığman gerekir; tek seansta bir kasa
 *    ~11 setten sonrası ölçülebilir fayda getirmiyor.
 *  - 4 gün → üst/alt. Her bölge haftada 2 kez.
 *  - 5–6 gün → itme/çekme/bacak. Her kalıp haftada 1.5–2 kez.
 *
 * Bu yüzden her şablonda `bestDays` var ve seçim gün sayısıyla uyuşmuyorsa
 * arayüz susmuyor, söylüyor.
 */
export type SplitId = 'full_ab' | 'upper_lower' | 'ppl'

export interface SplitDay {
  label: string
  name: string
  patterns: Pattern[]
}

export interface SplitDef {
  id: SplitId
  name: string
  short: string
  days: SplitDay[]
  bestDays: number[]
  note: string
}

export const SPLITS: SplitDef[] = [
  {
    id: 'full_ab',
    name: 'Tüm vücut (A / B)',
    short: 'Tüm vücut',
    bestDays: [2, 3],
    days: [
      { label: 'A', name: 'Tüm vücut A', patterns: DAY_A },
      { label: 'B', name: 'Tüm vücut B', patterns: DAY_B },
    ],
    note: 'Her seansta bir çömelme/menteşe, bir itme, bir çekme ve gövde. '
      + 'Haftada 2–3 gün çalışan biri için en verimli düzen: her kalıp haftada '
      + '1–1.5 kez geliyor ve hiçbir seans tıkanmıyor.',
  },
  {
    id: 'upper_lower',
    name: 'Üst / Alt',
    short: 'Üst-Alt',
    bestDays: [4],
    days: [
      { label: 'A', name: 'Üst vücut', patterns: ['push_h', 'pull_h', 'push_v', 'pull_v'] },
      { label: 'B', name: 'Alt vücut', patterns: ['squat', 'hinge', 'core', 'accessory'] },
    ],
    note: 'Üst gün dört itme/çekme kalıbını birden alıyor, alt gün bacak ve '
      + 'gövdeyi. Haftada 4 gün çalışıyorsan her bölge 2 kez geliyor — hacim '
      + 'için ideal aralık. 2 günde yaparsan her bölge haftada 1 kez kalır.',
  },
  {
    id: 'ppl',
    name: 'İtme / Çekme / Bacak',
    short: 'Push-Pull-Legs',
    bestDays: [3, 6],
    days: [
      { label: 'A', name: 'İtme', patterns: ['push_h', 'push_v', 'accessory'] },
      { label: 'B', name: 'Çekme', patterns: ['pull_h', 'pull_v', 'accessory'] },
      { label: 'C', name: 'Bacak', patterns: ['squat', 'hinge', 'core'] },
    ],
    note: 'Klasik üçlü. Haftada 3 günde her kalıp haftada 1 kez geliyor — '
      + 'büyüme için sınırda; asıl karşılığını haftada 6 günde (her şablon 2 kez) '
      + 'veriyor. Az günün varsa tüm vücut daha çok kazandırır.',
  },
]

export const SPLIT_BY_ID: Record<string, SplitDef> =
  Object.fromEntries(SPLITS.map((s) => [s.id, s]))

export function splitOf(id: string | null | undefined): SplitDef {
  return SPLIT_BY_ID[id ?? ''] ?? SPLITS[0]
}

/**
 * Seçilen şablonla haftalık gün sayısı uyuşuyor mu.
 * Uyuşmuyorsa yasak değil, uyarı — kararı kullanıcı veriyor.
 */
export function splitFit(split: SplitDef, daysPerWeek: number): string | null {
  if (split.bestDays.includes(daysPerWeek)) return null
  const perTemplate = daysPerWeek / split.days.length
  if (perTemplate < 1) {
    return `Haftada ${daysPerWeek} gün ile ${split.days.length} şablon var: her gün `
      + `ortalama ${perTemplate.toFixed(1)} kez geliyor, yani bazı bölgeler iki haftada bir `
      + `çalışılıyor. ${split.bestDays.join(' veya ')} gün öneriliyor.`
  }
  return `Bu düzen ${split.bestDays.join(' veya ')} gün için tasarlandı; sen haftada `
    + `${daysPerWeek} gün yapıyorsun. Çalışır ama en verimli hali bu değil.`
}

/** Reçete kalıba göre: bileşik hareketlerde tekrar aralığı, gövdede süre. */
/**
 * Kalıp başına set/tekrar/dinlenme. TEK KAYNAK — dışarıya da açık.
 *
 * Hacim önerisi (`hacimOneri.ts`) bu değerleri kendi içinde kopyalamıştı ve
 * hemen saptı: B gününde yatay itme kalıbı olmadığı için "tamamlayıcı" varsayıp
 * 2 set hesaplıyordu, oysa elle eklenen hareket kendi kalıbının reçetesiyle
 * (3 set) geliyor. Kullanıcıya "omuz +1.5 set" diye söz verilip +2.3 ekleniyordu.
 * Bir tablo iki yerde durursa er geç ikisi farklı olur.
 */
export const PRESCRIPTION: Record<Pattern, { sets: number; low: number; high: number; unit: 'rep' | 'sec'; rest: number }> = {
  squat:     { sets: 3, low: 8,  high: 12, unit: 'rep', rest: 90 },
  hinge:     { sets: 3, low: 8,  high: 12, unit: 'rep', rest: 90 },
  push_h:    { sets: 3, low: 8,  high: 12, unit: 'rep', rest: 90 },
  push_v:    { sets: 3, low: 8,  high: 12, unit: 'rep', rest: 75 },
  pull_h:    { sets: 3, low: 8,  high: 12, unit: 'rep', rest: 90 },
  pull_v:    { sets: 3, low: 5,  high: 10, unit: 'rep', rest: 90 },
  core:      { sets: 3, low: 20, high: 45, unit: 'sec', rest: 45 },
  accessory: { sets: 2, low: 12, high: 15, unit: 'rep', rest: 45 },
}

/**
 * Bir kalıbın dinlenme süresi. Antrenman kaydında ayrıca saklamıyoruz —
 * reçeteden türetiliyor, tek kaynak burası.
 *
 * 90–120 sn bilerek: Bayesçi meta-analiz (2024) 60 sn'nin ALTINI hipertrofi
 * için hafif olumsuz buluyor; 90 sn'nin ötesinde kayda değer fark yok.
 * "Dinlenmeyi kısaltarak yoğunluk artırma" yaygın ama kanıt bunu desteklemiyor.
 */
export function restFor(pattern: Pattern | string): number {
  return PRESCRIPTION[pattern as Pattern]?.rest ?? 90
}

const LEVEL_RANK: Record<string, number> = { beginner: 0, intermediate: 1, expert: 2 }

/**
 * Ekipman tercih sırası. Yüklenebilirliğe göre: dışarıdan ağırlık eklenebilen
 * hareket, ilerlemeye vücut ağırlığından daha uygun.
 */
const EQUIPMENT_RANK: Record<string, number> = {
  barbell: 6, dumbbell: 5, machine: 4, cable: 4, kettlebell: 3, bands: 2,
  pullup_bar: 1, ball: 1, bodyweight: 0,
}

export interface PlannedExercise {
  exercise: CatalogExercise
  pattern: Pattern
  sets: number
  low: number
  high: number
  unit: 'rep' | 'sec'
  restSeconds: number
  /** Aynı kalıp için kaç alternatif var — "başka hareket öner" bunu kullanıyor. */
  alternatives: number
  /**
   * `auto`: üreticinin kalıp listesinden koyduğu hareket — çıkarılabilir ama
   * çıkarınca o kalıp boşalır. `added`: kullanıcının elle eklediği.
   */
  source: 'auto' | 'added' | 'custom'
}

export interface WorkoutDay {
  /** Şablon harfi: A, B, C… Şablon sayısı seçilen bölünmeye göre değişiyor. */
  label: string
  /** Kullanıcının verdiği ad — yoksa "Antrenman A". */
  name?: string
  patterns: Pattern[]
  exercises: PlannedExercise[]
  /** Ekipman yetersizliği yüzünden doldurulamayan kalıplar — sessizce yutulmuyor. */
  missing: Pattern[]
  /** Kullanıcının bilerek çıkardığı kalıplar. `missing` ile aynı şey değil. */
  dropped: Pattern[]
  /** Kullanıcı bu güne dokundu mu — "öneri mi, senin programın mı" ayrımı. */
  edited: boolean
  /** Gün tamamen elle mi kuruldu — üretici bu gün için hiç çalışmadı. */
  custom: boolean
}

export interface ProgramInput {
  catalog: CatalogExercise[]
  equipment: string[]
  level?: Level
  /** Aynı kalıp için farklı hareket istendiğinde artan sayaç (kalıp bazlı). */
  variants?: Partial<Record<Pattern, number>>
  /**
   * Kullanıcının elle seçtiği hareket: `"A:squat" -> slug`.
   * Gün bazlı, çünkü aynı kalıp (gövde) iki günde de var ve ikisinde
   * farklı hareket seçilebilmeli.
   */
  picks?: Record<string, string>
  /** Kullanıcının gün bazlı düzenlemeleri: çıkardığı kalıplar, eklediği hareketler. */
  edits?: ProgramEdits
  /** Seçilen bölünme: tüm vücut, üst/alt, itme-çekme-bacak. Boşsa tüm vücut. */
  split?: SplitId | string
}

/**
 * Üretilen program bir **öneri**. Kullanıcı üstüne iki şey yazabiliyor:
 *  - `drop`: bu kalıbı bugün istemiyorum
 *  - `add`: şu hareketi de ekle (slug listesi, sıra korunur)
 *
 * Düzenleme "programın kopyası" olarak değil, üreticinin üstüne uygulanan
 * fark olarak saklanıyor: ekipman değişince ya da katalog güncellenince
 * program kendini yenilemeye devam etsin, kullanıcının kararı da kaybolmasın.
 */
export interface DayEdits {
  drop?: Pattern[]
  add?: string[]
  /**
   * Tamamen elle kurulmuş gün. Doluysa üretici bu gün için devre dışı kalıyor:
   * `drop`/`add` artık okunmuyor, gün birebir bu listeden çıkıyor.
   *
   * Üretici yine de arka planda çalışmaya devam ediyor ama sadece **uyarıyor**
   * (bkz. `coverageGaps`): "bu programda dikey çekme yok, kanat boşta". Karar
   * kullanıcının, bedelini söylemek bizim işimiz.
   */
  custom?: CustomDay
}

export interface CustomDay {
  /** Güne verilen ad — "İtme Günü" gibi. Boşsa "Antrenman A" yazıyor. */
  name?: string
  items: CustomItem[]
}

/** Elle kurulan gündeki bir satır. Reçete alanları boşsa kalıbın varsayılanı. */
export interface CustomItem {
  slug: string
  sets?: number
  low?: number
  high?: number
  rest?: number
}

/** Elle kurulan günde en fazla kaç hareket. 12 zaten ~70 dakika. */
export const MAX_CUSTOM_ITEMS = 12

export type ProgramEdits = Record<string, DayEdits | undefined>

/**
 * Gün başına elle eklenebilecek hareket sayısı. Sınır fizyolojik değil,
 * takvimsel: 4 ek hareket zaten ~20 dakika demek ve o noktada seans süresi
 * uyarısı devreye giriyor.
 */
export const MAX_ADDED = 4

/**
 * Bir kalıp için uygun adayları puana göre sıralar.
 * Deterministik — Math.random YOK. Aynı girdi her zaman aynı programı verir,
 * yoksa her render'da hareketler değişir ve test edilemez.
 */
export function candidatesFor(
  catalog: CatalogExercise[],
  pattern: Pattern,
  equipment: string[],
  level: Level = 'beginner',
): CatalogExercise[] {
  const have = new Set(equipment)
  const maxLevel = LEVEL_RANK[level] ?? 0

  return catalog
    .filter((x) => x.curated)
    .filter((x) => x.pattern === pattern)
    .filter((x) => have.has(x.equipment))
    .filter((x) => (LEVEL_RANK[x.level] ?? 0) <= maxLevel)
    .sort((a, b) => {
      // Önce yüklenebilirlik, sonra seviye yakınlığı, sonra ad (kararlılık için)
      const eq = (EQUIPMENT_RANK[b.equipment] ?? 0) - (EQUIPMENT_RANK[a.equipment] ?? 0)
      if (eq !== 0) return eq
      const lv = (LEVEL_RANK[b.level] ?? 0) - (LEVEL_RANK[a.level] ?? 0)
      if (lv !== 0) return lv
      // Sıralama orijinal ada göre: kullanıcıya gösterilen ad bu.
      return a.nameEn.localeCompare(b.nameEn, 'en')
    })
}

function planDay(day: SplitDay, input: ProgramInput): WorkoutDay {
  const { label, patterns } = day
  const level = input.level ?? 'beginner'
  const exercises: PlannedExercise[] = []
  const missing: Pattern[] = []

  const edits = input.edits?.[label] ?? {}
  if (edits.custom) return planCustomDay(label, edits.custom, input)

  const dropSet = new Set(edits.drop ?? [])
  const dropped = patterns.filter((p) => dropSet.has(p))

  for (const pattern of patterns) {
    if (dropSet.has(pattern)) continue

    const cands = candidatesFor(input.catalog, pattern, input.equipment, level)
    if (cands.length === 0) { missing.push(pattern); continue }

    // Aynı kalıp birden fazla günde varsa (gövde) farklı hareket gelsin diye
    // her şablona sırasına göre bir kaydırma ekleniyor.
    const offset = (input.variants?.[pattern] ?? 0) + LABELS.indexOf(label)
    // Elle seçim her zaman kazanır — ama sadece hâlâ yapılabiliyorsa.
    // Ekipman kaldırıldığında seçim sessizce düşer, program bozulmaz.
    const picked = input.picks?.[pickKey(label, pattern)]
    const ex = (picked ? cands.find((c) => c.slug === picked) : undefined) ?? cands[offset % cands.length]
    const p = PRESCRIPTION[pattern]

    exercises.push({
      exercise: ex, pattern,
      sets: p.sets, low: p.low, high: p.high, unit: p.unit,
      restSeconds: p.rest,
      alternatives: cands.length,
      source: 'auto',
    })
  }

  // Elle eklenenler sona geliyor: bileşik hareketler yorgunluktan önce yapılsın,
  // tamamlayıcılar sonra. Sıralamayı kullanıcıya bırakmak yerine kural koyuyoruz.
  const have = new Set(input.equipment)
  const added: string[] = []
  for (const slug of edits.add ?? []) {
    if (added.length >= MAX_ADDED) break
    if (exercises.some((e) => e.exercise.slug === slug)) continue   // aynı hareket iki kez olmaz
    const ex = input.catalog.find((c) => c.slug === slug)
    // Ekipman kaldırıldıysa ekleme sessizce düşer — picks ile aynı davranış.
    if (!ex || !have.has(ex.equipment)) continue

    const pattern = (PRESCRIPTION[ex.pattern as Pattern] ? ex.pattern : 'accessory') as Pattern
    const p = PRESCRIPTION[pattern]
    added.push(slug)
    exercises.push({
      exercise: ex, pattern,
      sets: p.sets, low: p.low, high: p.high, unit: p.unit,
      restSeconds: p.rest,
      alternatives: 0,
      source: 'added',
    })
  }

  return {
    label, name: day.name, patterns, exercises, missing, dropped,
    edited: dropped.length > 0 || added.length > 0,
    custom: false,
  }
}

/**
 * Elle kurulan gün. Üretici hiç devreye girmiyor: sıra, hareket ve reçete
 * birebir kullanıcının yazdığı gibi.
 */
function planCustomDay(label: string, custom: CustomDay, input: ProgramInput): WorkoutDay {
  const bySlug = new Map(input.catalog.map((c) => [c.slug, c]))
  const exercises: PlannedExercise[] = []
  const seen = new Set<string>()

  for (const item of custom.items.slice(0, MAX_CUSTOM_ITEMS)) {
    if (seen.has(item.slug)) continue
    const ex = bySlug.get(item.slug)
    // Ekipman süzgeci YOK: elle kurulan günde kullanıcı ne yazdıysa o duruyor.
    // Ekipman listesi program ÜRETİRKEN bir kısıt; burada üretim yok.
    if (!ex) continue
    seen.add(item.slug)

    const pattern = (PRESCRIPTION[ex.pattern as Pattern] ? ex.pattern : 'accessory') as Pattern
    const p = PRESCRIPTION[pattern]
    exercises.push({
      exercise: ex, pattern,
      sets: clamp(item.sets ?? p.sets, 1, 10),
      low: clamp(item.low ?? p.low, 1, 300),
      high: clamp(item.high ?? p.high, 1, 300),
      unit: p.unit,
      restSeconds: clamp(item.rest ?? p.rest, 0, 600),
      alternatives: 0,
      source: 'custom',
    })
  }

  return {
    label,
    name: custom.name?.trim() || undefined,
    patterns: [...new Set(exercises.map((e) => e.pattern))],
    exercises, missing: [], dropped: [], edited: true, custom: true,
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Math.round(n)))
}

/**
 * Programın tamamında hangi temel kalıplar hiç çalışılmıyor.
 *
 * Elle kurulan günde kapsama garantisi kalkıyor; garanti yerine **uyarı**
 * koyuyoruz. Yasaklamak yerine bedeli söylemek: adamın canı istemiyorsa
 * dikey çekme yapmayacak, ama kanadının boşta kaldığını bilerek yapsın.
 */
export function coverageGaps(days: WorkoutDay[]): Pattern[] {
  const covered = new Set(days.flatMap((d) => d.exercises.map((e) => e.pattern)))
  return [...new Set([...DAY_A, ...DAY_B])].filter((p) => !covered.has(p))
}

/**
 * Üretilen günü elle düzenlenebilir hâle çevirir — boş sayfayla başlamıyoruz.
 * Sıfırdan kurmak isteyen zaten hepsini silebilir; ama %90 durumda kullanıcı
 * "şunu çıkarayım, şunun setini artırayım" diyor ve boş liste onu cezalandırır.
 */
export function customFromDay(day: WorkoutDay): CustomDay {
  return {
    name: day.name,
    items: day.exercises.map((p) => ({
      slug: p.exercise.slug, sets: p.sets, low: p.low, high: p.high, rest: p.restSeconds,
    })),
  }
}

/** Listede bir satırı yukarı/aşağı taşır. Sınırda hiçbir şey yapmıyor. */
export function moveItem<T>(items: T[], from: number, delta: number): T[] {
  const to = from + delta
  if (from < 0 || from >= items.length || to < 0 || to >= items.length) return items
  const next = [...items]
  const [x] = next.splice(from, 1)
  next.splice(to, 0, x)
  return next
}

/** Elle seçim anahtarı — "A:squat". */
export function pickKey(label: string, pattern: Pattern): string {
  return `${label}:${pattern}`
}

/** Şablon harfleri — en fazla beş gün. */
export const LABELS = ['A', 'B', 'C', 'D', 'E']

export function generateProgram(input: ProgramInput): { days: WorkoutDay[]; equipmentUsed: string[] } {
  const split = splitOf(input.split)
  const days = split.days.map((d) => planDay(d, input))
  const used = [...new Set(days.flatMap((d) => d.exercises.map((e) => e.exercise.equipment)))].sort()
  return { days, equipmentUsed: used }
}

/** "3 × 8–12 tekrar" / "3 × 20–45 sn" */
export function prescriptionText(p: PlannedExercise): string {
  return `${p.sets} × ${p.low}–${p.high} ${p.unit === 'rep' ? 'tekrar' : 'sn'}`
}

export function muscleTr(m: string): string {
  return MUSCLE_TR[m] ?? m
}

/**
 * Seansın kabaca kaç dakika süreceği. Hareket eklerken/çıkarırken sonucun
 * takvimde bir karşılığı olduğu görünsün diye: "4 hareket daha ekleyeyim"
 * cümlesi 20 dakika demek ve bunu eklemeden önce bilmek gerekiyor.
 *
 * Tekrar başına ~3 sn (eksantrik+konsantrik), üstüne dinlenme, sonra %15
 * ısınma ve hareket arası geçiş payı.
 */
export function estimatedMinutes(day: WorkoutDay): number {
  const seconds = day.exercises.reduce((total, p) => {
    const work = p.unit === 'sec' ? p.high : p.high * 3
    return total + p.sets * (work + p.restSeconds)
  }, 0)
  return Math.round((seconds * 1.15) / 60)
}

export interface MuscleGroup {
  muscle: string
  label: string
  items: CatalogExercise[]
}

/**
 * Katalog kas bazında gruplanmış hali — "göğüs, omuz" diye aramak için.
 * Kalıp (push_h/pull_v) antrenman mantığının dili; kullanıcı hareketi
 * kas adıyla arıyor. İkisi de duruyor, seçim ekranı bunu kullanıyor.
 *
 * Birden fazla birincil kası olan hareket her iki grupta da görünüyor —
 * bench press hem göğüs hem triseps ararken çıkmalı.
 */
export function groupByMuscle(
  catalog: CatalogExercise[],
  o: {
    equipment: string[]
    level?: Level
    /** Zaten programda olanlar — listede tekrar görünmesinler. */
    exclude?: string[]
    /**
     * Küratörlü çekirdek set mi, katalogun tamamı mı.
     *
     * Üretici sadece küratörlü setten seçiyor: kalıbı ve Türkçe anlatımı olan,
     * davranışını bildiğimiz hareketler. Ama kullanıcı ELLE hareket ararken bu
     * daralma anlamsız — "elimde bant var" diyene 13 seçenek göstermek, 531
     * hareketlik katalogun üstünde oturup yok demek. Seviye süzgeci de burada
     * kalkıyor: seçimi kendisi yapıyorsa zorluğu da kendisi görsün, kartta
     * yazıyor.
     */
    curatedOnly?: boolean
  },
): MuscleGroup[] {
  const have = new Set(o.equipment)
  const curatedOnly = o.curatedOnly !== false
  const maxLevel = LEVEL_RANK[o.level ?? 'beginner'] ?? 0
  const skip = new Set(o.exclude ?? [])

  const usable = catalog
    .filter((x) => !curatedOnly || x.curated)
    .filter((x) => have.has(x.equipment))
    .filter((x) => !curatedOnly || (LEVEL_RANK[x.level] ?? 0) <= maxLevel)
    .filter((x) => !skip.has(x.slug))

  const seen = new Set<string>()
  const order = [...MUSCLE_ORDER, ...usable.flatMap((x) => x.primaryMuscles)]
    .filter((m) => (seen.has(m) ? false : (seen.add(m), true)))

  return order
    .map((muscle) => ({
      muscle,
      label: muscleTr(muscle),
      items: usable
        .filter((x) => x.primaryMuscles.includes(muscle))
        .sort((a, b) => {
          const eq = (EQUIPMENT_RANK[b.equipment] ?? 0) - (EQUIPMENT_RANK[a.equipment] ?? 0)
          return eq !== 0 ? eq : a.nameEn.localeCompare(b.nameEn, 'en')
        }),
    }))
    .filter((g) => g.items.length > 0)
}

/** Bir günün düzenlemesini güvenli şekilde günceller — sınırlar burada. */
export function editDay(
  edits: ProgramEdits, label: string, change: DayEdits,
): ProgramEdits {
  const cur = edits[label] ?? {}
  // `'custom' in change` bilerek: `custom: undefined` göndermek "elle kurulan
  // günü sil" demek, alanı hiç göndermemek "dokunma" demek. `??` ikisini
  // birbirine karıştırırdı.
  const custom = 'custom' in change ? change.custom : cur.custom
  const next: DayEdits = {
    drop: [...new Set(change.drop ?? cur.drop ?? [])],
    add: [...new Set(change.add ?? cur.add ?? [])].slice(0, MAX_ADDED),
    ...(custom ? { custom: { ...custom, items: custom.items.slice(0, MAX_CUSTOM_ITEMS) } } : {}),
  }
  const out = { ...edits, [label]: next }
  // Boş düzenlemeyi saklamıyoruz: "hiç dokunulmamış" ile "dokunulup geri
  // alınmış" aynı şey ve veride iz bırakmamalı.
  if (!next.drop?.length && !next.add?.length && !next.custom) delete out[label]
  return out
}

/** Görsel yolu — sunucu media/exercises altından servis ediyor. */
export function imageUrl(relative: string): string {
  return `/media/exercises/${relative}`
}
